import { ChangeDetectionStrategy, Component } from '@angular/core';
import { LtvQualifyingMortgage, isEncumbrance } from '@models/encumbrance';
import { LTV_SOURCE } from '@models/matter';
import { MatterMortgage } from '@models/mortgage';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { NavigationService } from '@services/nav/nav.service';
import { UrlService } from '@services/url/url.service';
import { MatterDispatch } from '@state/matter';
import { ltvSourceLabels } from '@state/matter/ltv/ltv.models';
import { LtvSelectors } from '@state/matter/ltv/ltv.selectors';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { Observable } from 'rxjs';
import { SelectionGroup } from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { TestHooks } from './value-and-ltv.hooks';

@Component({
	selector: 'app-value-and-ltv',
	templateUrl: './value-and-ltv.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class ValueAndLtvComponent {
	testHooks = TestHooks;
	@SelectSnapshot(SelectedMatter.id) matterId!: string;

	@Select(LtvSelectors.customSource) customSource$!: Observable<string>;

	@Select(LtvSelectors.showOnPropertiesPage)
	showOnPropertiesPage$!: Observable<boolean>;

	@Select(LtvSelectors.isOutstandingNotEqualToCalculated)
	isOutstandingNotEqualToCalculated$!: Observable<boolean>;

	@Select(LtvSelectors.ltvPercentage) ltvPercentage$!: Observable<number>;

	@Select(LtvSelectors.qualifyingMortgages) mortgages$!: Observable<
		LtvQualifyingMortgage[]
	>;

	@Select(LtvSelectors.outstanding) outstanding$!: Observable<number>;

	@Select(LtvSelectors.propertyValue) propertyValue$!: Observable<number>;

	@Select(LtvSelectors.showLtvData) showLtvData$!: Observable<boolean>;

	@Select(LtvSelectors.showPropertyValueSetToAps)
	showPropertyValueSetToAps$!: Observable<boolean>;

	@Select(LtvSelectors.isCustomSource) showCustomSource$!: Observable<boolean>;

	@Select(LtvSelectors.sourceOptions) sourceOptions$!: Observable<
		SelectionGroup[]
	>;

	@Select(LtvSelectors.source) source$!: Observable<LTV_SOURCE>;

	@SelectSnapshot(LtvSelectors.swapPropertyValuesForRefi)
	swapPropertyValuesForRefi!: boolean;

	@SelectSnapshot(SelectedMatter.purchasePrice) matterSalePrice!: number;

	ltvSourceLabels = ltvSourceLabels;

	trackMortgageBy = (mortgage: MatterMortgage) => mortgage?.id;

	constructor(
		private navigation: NavigationService,
		private analytics: AnalyticsService,
		private matterDispatch: MatterDispatch,
	) {}

	onCustomSourceChanged(value: string) {
		this.matterDispatch.LtvUpdateCustomSource(value);
	}

	onMortgageClick(mortgage: LtvQualifyingMortgage) {
		const encumbrance = isEncumbrance(mortgage);
		this.navigation.goToUrl(
			isEncumbrance(mortgage)
				? UrlService.Matter_Encumbrances_Encumbrance(this.matterId, mortgage.id)
				: UrlService.Matter_Mortgages_Mortgage(this.matterId, mortgage.id),
		);
		this.analytics.logEvent(
			AnalyticsEvent.LTV_MATTER_PROPERTY_MORTGAGE_TILE_CLICK,
			{
				[encumbrance ? 'encumbranceId' : 'mortgageId']: mortgage.id,
			},
		);
	}

	onOutstandingChanged(value: number) {
		this.matterDispatch.LtvUpdateOutstanding(value);
	}

	onPropertyValueChanged(value: number) {
		this.matterDispatch.LtvUpdatePropertyValue(value);
	}

	onSetToCalculatedValueClick() {
		this.matterDispatch.LtvUpdateOutstanding(null);
	}

	onSetToApsPropertyValueClick() {
		this.matterDispatch.LtvSetPropertyValueToSalePrice();
	}

	onSourceSelect(value: LTV_SOURCE) {
		this.matterDispatch.LtvUpdateSource(value);
	}
}
